import React, { useEffect, useMemo, useState } from 'react';
import {
  Divider,
  Grid,
  Header,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
  SemanticCOLORS
} from 'semantic-ui-react';

import Button from '../../../components/Button';
import { dangerNotification } from '../../../services/notification';
import { maskCpfOrCnpj, maskPhoneNumber } from '../../../services/masks';
import {
  getStandaloneChargeCommissionCollaborator,
  StandaloneChargeCommissionCollaboratorDTO,
  StandaloneChargeCommissionCollaboratorStatus
} from '../../../services/standalone-charges';
import FieldBlock from './FieldBlock';

import ActivationRegistrationSections from './ActivationRegistrationSections';

type Props = {
  open: boolean;
  collaboratorId: string | null;
  hideEstateSection?: boolean;
  onClose: () => void;
  onRegisterBiometry?: (collaboratorId: string) => void;
};

const statusLabelMap: Record<StandaloneChargeCommissionCollaboratorStatus, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  AWAITING_BIOMETRY: 'Aguardando biometria',
  BIOMETRY_REJECTED: 'Biometria recusada',
  SUPPORT_REQUIRED: 'Análise necessária'
};

const statusLabelColor: Record<StandaloneChargeCommissionCollaboratorStatus, string> = {
  ACTIVE: 'green',
  INACTIVE: 'grey',
  AWAITING_BIOMETRY: 'yellow',
  BIOMETRY_REJECTED: 'red',
  SUPPORT_REQUIRED: 'orange'
};

const pickCollaboratorActivationSource = (
  detail: StandaloneChargeCommissionCollaboratorDTO
) => {
  const activation = detail.activation || {};
  return {
    recipientType: activation.recipientType,
    registerInformation: activation.registerInformation,
    defaultBankAccount: activation.defaultBankAccount,
    payload: activation.payload,
    pagarmeRecipientId: activation.pagarmeRecipientId,
    lastSyncAt: activation.lastSyncAt,
    lastSyncStatus: activation.lastSyncStatus,
    lastSyncError: activation.lastSyncError,
    completedAt: activation.completedAt,
    createdAt: detail.created_at,
    updatedAt: detail.updated_at
  };
};

const CollaboratorDetailsModal: React.FC<Props> = ({
  open,
  collaboratorId,
  hideEstateSection = false,
  onClose,
  onRegisterBiometry
}) => {
  const [detail, setDetail] = useState<StandaloneChargeCommissionCollaboratorDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !collaboratorId) {
      setDetail(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getStandaloneChargeCommissionCollaborator(collaboratorId)
      .then(data => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Não foi possível carregar os detalhes do colaborador.');
          dangerNotification('Erro', 'Não foi possível carregar os detalhes do colaborador.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, collaboratorId]);

  const activationSource = useMemo(
    () => (detail ? pickCollaboratorActivationSource(detail) : null),
    [detail]
  );

  const showBiometryAction = detail?.status === 'AWAITING_BIOMETRY';
  const showRetryBiometryAction = detail?.status === 'BIOMETRY_REJECTED';

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>Detalhes do colaborador</Modal.Header>
      <Modal.Content scrolling>
        {loading && (
          <Segment basic style={{ minHeight: 160 }}>
            <Loader active inline="centered">
              Carregando…
            </Loader>
          </Segment>
        )}
        {!loading && error && <Message negative>{error}</Message>}
        {!loading && !error && detail && activationSource && (
          <>
            <Header as="h4" style={{ marginTop: 0, marginBottom: 16 }}>
              Resumo
            </Header>
            <Grid stackable columns={3}>
              <Grid.Column width={5}>
                <FieldBlock label="Nome">{detail.user?.name || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Status">
                  <Label
                    color={statusLabelColor[detail.status] as SemanticCOLORS}
                    size="small"
                  >
                    {statusLabelMap[detail.status] || detail.status}
                  </Label>
                </FieldBlock>
              </Grid.Column>
              <Grid.Column width={7}>
                <FieldBlock label="Responsável">{detail.responsable?.name || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Documento">
                  {detail.user?.doc ? maskCpfOrCnpj(detail.user.doc) : '-'}
                </FieldBlock>
              </Grid.Column>
              <Grid.Column width={5}>
                <FieldBlock label="E-mail">{detail.user?.email || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={7}>
                <FieldBlock label="Telefone">
                  {detail.user?.cellphone ? maskPhoneNumber(detail.user.cellphone) : '-'}
                </FieldBlock>
              </Grid.Column>
            </Grid>

            {!hideEstateSection && (
              <>
                <Divider section />
                <Header as="h4" style={{ marginBottom: 16 }}>
                  Imobiliária
                </Header>
                <Grid stackable columns={2}>
                  <Grid.Column>
                    <FieldBlock label="Nome">{detail.estate?.name || '-'}</FieldBlock>
                  </Grid.Column>
                  <Grid.Column>
                    <FieldBlock label="ID">
                      <code style={{ fontSize: '0.9em' }}>
                        {detail.estate?.id || detail.estateId || '-'}
                      </code>
                    </FieldBlock>
                  </Grid.Column>
                </Grid>
              </>
            )}

            <Divider section />

            <ActivationRegistrationSections
              source={activationSource}
              createdAt={detail.created_at}
              updatedAt={detail.updated_at}
            />

            {showBiometryAction && onRegisterBiometry && collaboratorId && (
              <>
                <Divider section />
                <Message info>
                  <p style={{ margin: 0 }}>
                    A biometria ainda está pendente. O colaborador precisa concluir a prova de vida
                    para receber comissões.
                  </p>
                </Message>
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="button"
                    size="small"
                    text="Cadastrar biometria"
                    onClick={() => onRegisterBiometry(collaboratorId)}
                  />
                </div>
              </>
            )}

            {showRetryBiometryAction && onRegisterBiometry && collaboratorId && (
              <>
                <Divider section />
                <Message negative role="alert">
                  <p style={{ margin: 0 }}>
                    A biometria foi recusada na Pagar.me. É necessário iniciar um novo cadastro
                    de recebedor para tentar novamente.
                  </p>
                </Message>
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="button"
                    size="small"
                    text="Tentar biometria novamente"
                    onClick={() => onRegisterBiometry(collaboratorId)}
                  />
                </div>
              </>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CollaboratorDetailsModal;
